/**
 * MobileOnboarding — 3-slide swipable onboarding carousel that
 * introduces Food Pulse's core value props before the user lands on
 * the home dashboard.
 *
 * Routes: /m/welcome/:step (1 | 2 | 3)
 * Previous/Next/Skip footer. Last slide swaps Next for Get Started.
 */
import { useNavigate, useParams } from "react-router-dom";

const FP_GREEN_BG = "#CBEB90";
const FP_ACCENT = "#7BB325";

const SLIDES = [
  {
    id: 1,
    title: "Donate Excess Food",
    subtitle: "Where surplus meets need—in real time.",
    body: "Supermarkets, grocers, restaurants, and caterers can now connect instantly with local food banks and soup kitchens. Use Food Pulse to share real-time updates of edible surplus, reduce disposal costs, and nourish those in need instead of the landfill.",
    image: "https://images.pexels.com/photos/6591162/pexels-photo-6591162.jpeg?auto=compress&cs=tinysrgb&w=1200",
    imageAlt: "Volunteer handing a donation box of food",
  },
  {
    id: 2,
    title: "Find Local Deals",
    subtitle: "Never miss a markdown again.",
    body: "Grocers frequently cut prices to move perishables—Food Pulse makes sure you're the first to know. Get timely alerts on discounts, price drops, and clearance sales nearby. Save money, fight waste, and shop sustainably every day.",
    image: "https://images.pexels.com/photos/2255935/pexels-photo-2255935.jpeg?auto=compress&cs=tinysrgb&w=1200",
    imageAlt: "Colorful produce section at a supermarket",
  },
  {
    id: 3,
    title: "Reducing Food Waste is Our Mission",
    subtitle: "40% of food is wasted—but together, we can change that.",
    body: "Every year, $165 billion worth of edible food goes to waste in the U.S. alone. At Food Pulse, we're building a connected, conscious community that reduces food waste from farms to fridges. Join us in this powerful mission. Build Good Karma, create Social Capital, and help feed a better future.",
    image: "https://images.pexels.com/photos/6591160/pexels-photo-6591160.jpeg?auto=compress&cs=tinysrgb&w=1200",
    imageAlt: "Person scraping leftover food into a trash bag",
  },
  {
    id: 4,
    title: "Food Pulse Ambassadors",
    subtitleBold: "As a Food Pulse Ambassador, you will:",
    bullets: [
      "Set up and grow community Food Pulse units",
      "Help local vendors join and manage their store on Food Pulse",
      "Coordinate food donations, pickups, and deliveries to families and charities",
      "Receive free food items or offers from vendors",
      "Earn social recognition and build your community impact",
    ],
  },
];

// Guard to lift the max step
const MAX_STEP = SLIDES.length;


export default function MobileOnboarding() {
  const { step } = useParams();
  const nav = useNavigate();
  const idx = Math.max(1, Math.min(MAX_STEP, parseInt(step || "1", 10)));
  const slide = SLIDES[idx - 1];
  const isFirst = idx === 1;
  const isLast = idx === SLIDES.length;

  const finish = () => {
    localStorage.setItem("fp_mobile_onboarded", "1");
    nav("/m", { replace: true });
  };
  const next = () => (isLast ? finish() : nav(`/m/welcome/${idx + 1}`));
  const prev = () => nav(`/m/welcome/${idx - 1}`);

  return (
    <div
      className="min-h-screen flex flex-col items-center px-6 pt-10 pb-8"
      style={{ background: FP_GREEN_BG }}
      data-testid={`mobile-onboarding-${idx}`}
    >
      {/* Brand */}
      <div className="flex items-center gap-3 mb-8">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: "#653091" }}
        >
          <span style={{ color: "#9CCA45", fontFamily: "serif", fontWeight: 700 }}>FP</span>
        </div>
        <span className="text-2xl font-bold" style={{ color: "#653091" }}>Food Pulse</span>
      </div>

      {/* Hero image (only for image slides) */}
      {slide.image && (
        <div className="w-full max-w-md rounded-3xl overflow-hidden shadow-md mb-8">
          <img
            src={slide.image}
            alt={slide.imageAlt}
            loading="lazy"
            className="w-full h-56 object-cover"
            data-testid={`onboarding-image-${idx}`}
          />
        </div>
      )}

      {/* Copy */}
      <h1
        className="text-3xl font-bold text-center mb-3 leading-tight"
        style={{ color: FP_ACCENT }}
        data-testid={`onboarding-title-${idx}`}
      >
        {slide.title}
      </h1>
      {slide.subtitle && (
        <p className="text-lg font-semibold text-center mb-4" data-testid={`onboarding-subtitle-${idx}`}>
          {slide.subtitle}
        </p>
      )}
      {slide.subtitleBold && (
        <p className="text-lg font-bold text-center mb-4 self-start" data-testid={`onboarding-subtitle-${idx}`}>
          {slide.subtitleBold}
        </p>
      )}
      {slide.body && (
        <p className="text-base text-center leading-relaxed max-w-md" data-testid={`onboarding-body-${idx}`}>
          {slide.body}
        </p>
      )}
      {slide.bullets && (
        <ul className="w-full max-w-md space-y-4 text-base leading-snug" data-testid={`onboarding-bullets-${idx}`}>
          {slide.bullets.map((b, i) => (
            <li key={i}>{b}</li>
          ))}
        </ul>
      )}

      {/* Progress dots */}
      <div className="mt-8 flex gap-2">
        {SLIDES.map((s) => (
          <span
            key={s.id}
            className="w-2 h-2 rounded-full"
            style={{ background: s.id === idx ? "#653091" : "rgba(101,48,145,0.25)" }}
          />
        ))}
      </div>

      {/* Nav */}
      <div className="mt-auto pt-10 w-full flex items-center justify-between gap-3 flex-wrap">
        <button
          disabled={isFirst}
          onClick={prev}
          data-testid="onboarding-previous"
          className="px-6 py-3 rounded-full border border-black text-sm font-medium disabled:opacity-30 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        {!isLast && (
          <button
            onClick={next}
            data-testid="onboarding-next"
            className="px-6 py-3 rounded-full border border-black text-sm font-medium"
          >
            Next
          </button>
        )}
        <button
          onClick={finish}
          data-testid="onboarding-skip"
          className="px-6 py-3 rounded-full border border-black text-sm font-medium"
        >
          {isLast ? "Get started" : "Skip"}
        </button>
      </div>
    </div>
  );
}
